import { Link, useLocation } from "wouter";
import { useEffect, useState } from "react";
import { 
  Store,
  ChevronDown,
  LogOut,
  Home,
  Shield,
  Check,
  Plus,
  TrendingUp,
  MoreHorizontal,
  Inbox,
} from "lucide-react";
import eatzyLogo from "@/assets/eatzy-teal-disc-1024.png";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";

function useUserRole() {
  const { data } = useQuery<{ role: string }>({
    queryKey: ['/api/user/role'],
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  return data?.role;
}

const bottomTabs = [
  { href: "/partner", label: "Home", icon: Home, exact: true },
  { href: "/partner/performance", label: "Performance", icon: TrendingUp, exact: false },
  { href: "/partner/post?quickMode=1", label: "QuickPost", icon: Plus, exact: false, isCenter: true },
  { href: "/partner/inbox", label: "Inbox", icon: Inbox, exact: false },
  { href: "/partner/tools", label: "More", icon: MoreHorizontal, exact: false },
];

interface Restaurant {
  id: number;
  profile: string;
  name: string;
  business: number;
  address: string;
  phone?: string;
  timezone: string;
  active?: boolean;
  kudozUuid?: string | null;
}

export default function PartnerLayout({ children }: { children: React.ReactNode }) {
  const [location, navigate] = useLocation();
  const [isInitialized, setIsInitialized] = useState(false);
  const [autoInitializing, setAutoInitializing] = useState(false);
  const [eatzyToken, setEatzyToken] = useState<string | null>(() => sessionStorage.getItem('partnerEatzyToken'));
  const [isAutoAuthFetching, setIsAutoAuthFetching] = useState(() => !sessionStorage.getItem('partnerEatzyToken'));
  const [restaurantPopoverOpen, setRestaurantPopoverOpen] = useState(false);
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const userRole = useUserRole();
  const isAdmin = userRole === 'admin' || userRole === 'super_admin';
  
  const currentRestaurantId = localStorage.getItem('partnerRestaurantId');
  const currentRestaurantName = localStorage.getItem('partnerRestaurantName');

  useEffect(() => {
    if (isAutoAuthFetching) {
      const authToken = localStorage.getItem('auth_token');
      fetch('/api/partner/restmanager/auth', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authToken ? `Bearer ${authToken}` : '',
        },
      })
        .then(r => r.json())
        .then(data => {
          if (data.accessToken) {
            sessionStorage.setItem('partnerEatzyToken', data.accessToken);
            setEatzyToken(data.accessToken);
            setIsAutoAuthFetching(false);
          } else {
            navigate('/partner/login');
          }
        })
        .catch(() => navigate('/partner/login'));
      return;
    }
    if (!eatzyToken) {
      navigate('/partner/login');
      return;
    }
    if (!currentRestaurantId && !autoInitializing) {
      setAutoInitializing(true);
    }
  }, [isAutoAuthFetching, eatzyToken, currentRestaurantId, navigate, autoInitializing]);
  
  const { data: restaurants, isLoading: restaurantsLoading, isError: restaurantsError, refetch: refetchRestaurants } = useQuery<Restaurant[]>({
    queryKey: ['/api/partner/restmanager/restaurants'],
    queryFn: async () => {
      const token = localStorage.getItem('auth_token');
      const currentToken = sessionStorage.getItem('partnerEatzyToken');
      const response = await fetch('/api/partner/restmanager/restaurants', {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'X-Eatzy-Token': currentToken || '',
        },
      });
      if (response.status === 401 || response.status === 403) {
        sessionStorage.removeItem('partnerEatzyToken');
        setEatzyToken(null);
        setIsAutoAuthFetching(true);
        throw new Error('Token expired');
      }
      if (!response.ok) throw new Error('Failed to fetch restaurants');
      return response.json();
    },
    enabled: !!eatzyToken,
    retry: 1,
  });
  
  const currentRestaurant = restaurants?.find(r => r.id.toString() === currentRestaurantId) || restaurants?.[0];
  
  useEffect(() => {
    if (currentRestaurant && (!isInitialized || autoInitializing)) {
      localStorage.setItem('partnerRestaurantId', currentRestaurant.id.toString());
      localStorage.setItem('partnerRestaurantName', currentRestaurant.name);
      localStorage.setItem('partnerRestaurantProfile', currentRestaurant.profile);
      if (currentRestaurant.kudozUuid) {
        localStorage.setItem('partnerKudozRestaurantUuid', currentRestaurant.kudozUuid);
      } else {
        localStorage.removeItem('partnerKudozRestaurantUuid');
      }
      setIsInitialized(true);
      setAutoInitializing(false);
    }
  }, [currentRestaurant, isInitialized, autoInitializing]);
  
  const handleRestaurantChange = (restaurant: Restaurant) => {
    localStorage.setItem('partnerRestaurantId', restaurant.id.toString());
    localStorage.setItem('partnerRestaurantName', restaurant.name);
    localStorage.setItem('partnerRestaurantProfile', restaurant.profile);
    if (restaurant.kudozUuid) {
      localStorage.setItem('partnerKudozRestaurantUuid', restaurant.kudozUuid);
    } else {
      localStorage.removeItem('partnerKudozRestaurantUuid');
    }
    window.location.reload();
  };
  
  const handleLogout = () => {
    sessionStorage.removeItem('partnerEatzyToken');
    localStorage.removeItem('partnerRestaurantId');
    localStorage.removeItem('partnerRestaurantName');
    localStorage.removeItem('partnerRestaurantProfile');
    localStorage.removeItem('partnerUserId');
    localStorage.removeItem('partnerKudozRestaurantUuid');
    setEatzyToken(null);
    navigate('/partner/login');
  };
  
  if (isAutoAuthFetching) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3">
        <Store className="h-8 w-8 text-primary animate-pulse" />
        <p className="text-sm text-muted-foreground">Setting up portal access...</p>
      </div>
    );
  }

  if (!eatzyToken) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3">
        <Store className="h-8 w-8 text-primary animate-pulse" />
        <p className="text-sm text-muted-foreground">Redirecting to login...</p>
      </div>
    );
  }
  
  if (autoInitializing || (!currentRestaurantId && restaurantsLoading)) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3">
        <Store className="h-8 w-8 text-primary animate-pulse" />
        <p className="text-sm text-muted-foreground">Loading your restaurant...</p>
      </div>
    );
  }
  
  if (restaurantsError) {
    const tokenGone = !sessionStorage.getItem('partnerEatzyToken');
    if (tokenGone) {
      navigate('/partner/login');
      return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-3">
          <Store className="h-8 w-8 text-primary animate-pulse" />
          <p className="text-sm text-muted-foreground">Redirecting to login...</p>
        </div>
      );
    }
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 text-center">
        <Store className="h-8 w-8 text-muted-foreground" />
        <div className="text-lg font-semibold">Unable to load restaurants</div>
        <p className="text-sm text-muted-foreground max-w-md">
          We couldn't fetch your restaurant data. This may be a temporary issue.
        </p>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => refetchRestaurants()}>Try Again</Button>
          <Button variant="ghost" onClick={handleLogout}>Sign Out</Button>
        </div>
      </div>
    );
  }
  
  if (!currentRestaurantId && !restaurantsLoading && restaurants && restaurants.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 text-center">
        <Store className="h-8 w-8 text-muted-foreground" />
        <div className="text-lg font-semibold">No restaurants found</div>
        <p className="text-sm text-muted-foreground max-w-md">
          Your account doesn't have any restaurants associated with it. Please contact support.
        </p>
        <Button variant="ghost" onClick={handleLogout}>Sign Out</Button>
      </div>
    );
  }
  
  if (!currentRestaurantId && !restaurantsLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3">
        <Store className="h-8 w-8 text-primary animate-pulse" />
        <p className="text-sm text-muted-foreground">Selecting restaurant...</p>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background pb-20">
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex h-14 items-center gap-4 px-4">
          <Link href="/partner">
            <span className="flex items-center gap-2 cursor-pointer" data-testid="link-partner-home">
              <img src={eatzyLogo} alt="Eatzy" className="h-7 w-7 object-contain" />
              <span className="font-semibold text-base text-eatzy-teal">Partner Portal</span>
            </span>
          </Link>
          
          {(() => {
            const restaurantCount = restaurants?.length ?? 0;
            const activeCount = (restaurants ?? []).filter(r => r.active !== false).length;
            const filteredRestaurants = (restaurants ?? []).filter(r =>
              !showActiveOnly || r.active !== false
            );
            const triggerLabel = currentRestaurantName || currentRestaurant?.name || 'Select Restaurant';

            if (restaurantCount <= 1) {
              return (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="ml-4 max-w-[220px]" data-testid="button-restaurant-selector">
                      <span className="truncate">{triggerLabel}</span>
                      <ChevronDown className="ml-2 h-4 w-4 flex-shrink-0" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                      <LogOut className="h-4 w-4 mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            }

            if (restaurantCount <= 10) {
              return (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="ml-4 max-w-[220px]" data-testid="button-restaurant-selector">
                      <span className="truncate">{triggerLabel}</span>
                      <ChevronDown className="ml-2 h-4 w-4 flex-shrink-0" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    {restaurants?.map((restaurant) => (
                      <DropdownMenuItem
                        key={restaurant.id}
                        onClick={() => handleRestaurantChange(restaurant)}
                        data-testid={`menu-item-restaurant-${restaurant.id}`}
                      >
                        <Check className={cn("mr-2 h-4 w-4", currentRestaurantId === restaurant.id.toString() ? "opacity-100" : "opacity-0")} />
                        {restaurant.name}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                      <LogOut className="h-4 w-4 mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            }

            return (
              <Popover open={restaurantPopoverOpen} onOpenChange={setRestaurantPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="ml-4 max-w-[220px]" data-testid="button-restaurant-selector">
                    <span className="truncate">{triggerLabel}</span>
                    <ChevronDown className="ml-2 h-4 w-4 flex-shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search restaurants..." />
                    <div className="flex items-center justify-between px-3 py-1.5 border-b">
                      <span className="text-xs text-muted-foreground">
                        {showActiveOnly
                          ? `Active (${activeCount} of ${restaurantCount})`
                          : `All (${restaurantCount})`}
                      </span>
                      <button
                        className="text-xs text-primary font-medium hover:underline"
                        onClick={() => setShowActiveOnly(v => !v)}
                      >
                        {showActiveOnly ? 'Show All' : 'Active Only'}
                      </button>
                    </div>
                    <CommandList>
                      <CommandEmpty>No restaurants found.</CommandEmpty>
                      <CommandGroup>
                        {filteredRestaurants.map((restaurant) => (
                          <CommandItem
                            key={restaurant.id}
                            value={restaurant.name}
                            onSelect={() => {
                              handleRestaurantChange(restaurant);
                              setRestaurantPopoverOpen(false);
                            }}
                            data-testid={`menu-item-restaurant-${restaurant.id}`}
                          >
                            <Check className={cn("mr-2 h-4 w-4 flex-shrink-0", currentRestaurantId === restaurant.id.toString() ? "opacity-100" : "opacity-0")} />
                            <span className="truncate flex-1">{restaurant.name}</span>
                            {restaurant.active === false && (
                              <span className="ml-2 text-xs text-muted-foreground flex-shrink-0">inactive</span>
                            )}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                      <CommandSeparator />
                      <CommandGroup>
                        <CommandItem onSelect={handleLogout} className="text-destructive">
                          <LogOut className="mr-2 h-4 w-4" />
                          Sign Out
                        </CommandItem>
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            );
          })()}
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-1.5" data-testid="button-portal-switcher">
                <Store className="h-4 w-4" />
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem asChild>
                <Link href="/">
                  <Home className="h-4 w-4 mr-2" />
                  User App
                </Link>
              </DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem asChild>
                  <Link href="/admin">
                    <Shield className="h-4 w-4 mr-2" />
                    Admin Portal
                  </Link>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
      
      <main className="container mx-auto py-6 px-4">
        {children}
      </main>

      <nav className="fixed bottom-0 left-0 right-0 z-50 h-16 bg-background border-t flex items-stretch safe-area-padding-bottom">
        {bottomTabs.map((tab) => {
          const tabPath = tab.href.split('?')[0];
          const isActive = tab.exact
            ? location === tabPath
            : location === tabPath || location.startsWith(tabPath + '/') || location.startsWith(tabPath + '?');

          if (tab.isCenter) {
            return (
              <button
                key={tab.href}
                type="button"
                className="flex-1 flex flex-col items-center justify-center gap-0.5"
                data-testid="tab-quickpost"
                onClick={() => {
                  if (isActive) {
                    // Already on the post page — re-open the type selector
                    window.dispatchEvent(new CustomEvent('quickpost:open'));
                  } else {
                    navigate(tab.href);
                  }
                }}
              >
                <div className={cn(
                  "w-12 h-12 rounded-full flex items-center justify-center -mt-5 shadow-lg transition-transform",
                  isActive
                    ? "bg-primary text-primary-foreground scale-110"
                    : "bg-primary text-primary-foreground"
                )}>
                  <Plus className="h-6 w-6" />
                </div>
                <span className={cn(
                  "text-[10px] font-semibold -mt-0.5",
                  isActive ? "text-primary" : "text-muted-foreground"
                )}>Post</span>
              </button>
            );
          }

          return (
            <Link
              key={tab.href}
              href={tab.href}
              className={cn(
                "flex-1 flex flex-col items-center justify-center gap-0.5 text-xs font-medium transition-colors",
                isActive
                  ? "text-eatzy-teal border-t-2 border-t-eatzy-teal"
                  : "text-muted-foreground border-t-2 border-t-transparent"
              )}
              data-testid={`tab-${tab.label.toLowerCase()}`}
            >
              <tab.icon className={cn("h-5 w-5", isActive ? "stroke-[2.5]" : "stroke-[1.5]")} />
              <span>{tab.label}</span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
